import { useRef, type KeyboardEvent } from 'react';

/**
 * KEYBOARD NAVIGATION FOR A role="tablist" — the WAI-ARIA pattern the page tabs
 * declared the roles for but never implemented (interaction-witness F4). Arrow
 * keys (and Home/End) move BOTH the selection and the focus to the next tab, so a
 * keyboard or screen-reader user drives the tabs the way every tablist should,
 * not only by Tab-to-focus + Enter. Mirrors the document-type radio group in
 * Documents.tsx.
 *
 * Usage, on a role="tablist" whose buttons are role="tab":
 *   const { onKeyDown, setRef, tabIndex } = useTabKeys(ORDER, current, setCurrent);
 *   <div className="tabs" role="tablist" onKeyDown={onKeyDown}>
 *     {ORDER.map((id) => (
 *       <button role="tab" aria-selected={id === current}
 *         ref={setRef(id)} tabIndex={tabIndex(id)} onClick={() => setCurrent(id)}>…</button>
 *     ))}
 *   </div>
 *
 * The roving tabindex keeps the whole strip a single tab stop (only the selected
 * tab is tabbable); the arrows walk the rest, wrapping at the ends.
 */
export function useTabKeys<T extends string>(
  order: readonly T[],
  current: T,
  setCurrent: (t: T) => void,
) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const setRef = (id: T) => (el: HTMLButtonElement | null) => {
    refs.current[id] = el;
  };
  const moveTo = (t: T) => {
    setCurrent(t);
    refs.current[t]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const i = order.indexOf(current);
    if (i < 0) return;
    const n = order.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveTo(order[(i + 1) % n]); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveTo(order[(i - 1 + n) % n]); }
    else if (e.key === 'Home') { e.preventDefault(); moveTo(order[0]); }
    else if (e.key === 'End') { e.preventDefault(); moveTo(order[n - 1]); }
  };
  /** Roving tabindex — only the selected tab is in the tab order. */
  const tabIndex = (id: T) => (id === current ? 0 : -1);
  return { onKeyDown, setRef, tabIndex };
}
